import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  FlaskConical,
  Globe,
  History,
  Loader2,
  Paperclip,
  Pause,
  Pencil,
  Play,
  Send,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  useDeprovisionAgentMailboxSelf,
  useProvisionAgentMailboxSelf,
  useTestAgentMailboxSelf,
  useUpdateAgentMailboxSelf,
} from "@/hooks/agent/useAgentMailboxSelf";
import { EmailForwardingSteps } from "./EmailForwardingSteps";
import { ListenerEmailForwarding } from "./ListenerEmailForwarding";

import type { AgentMailbox, AgentMailboxStatus } from "@/types/agent-mailboxes";

const STATUS_LABELS: Record<AgentMailboxStatus, string> = {
  pending: "Pending",
  provisioned: "Active",
  suspended: "Suspended",
  failed: "Failed",
  unavailable: "Unavailable",
};

const getStatusBadge = (status: AgentMailboxStatus, brand: string) => {
  switch (status) {
    case "provisioned":
      return (
        <Badge className="border-0" style={{ backgroundColor: `${brand}15`, color: brand }}>
          {STATUS_LABELS[status]}
        </Badge>
      );
    case "suspended":
      return <Badge variant="outline">{STATUS_LABELS[status]}</Badge>;
    case "pending":
      return <Badge variant="secondary">{STATUS_LABELS[status]}</Badge>;
    case "failed":
      return <Badge variant="destructive">{STATUS_LABELS[status]}</Badge>;
    case "unavailable":
      return <Badge variant="outline">{STATUS_LABELS[status]}</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const LOCALPART_RE = /^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const slugify = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");

interface GenieMailboxSectionProps {
  agentId: string | undefined;
  agentName: string;
  mailbox: AgentMailbox | null;
  brand?: string;
  /** A Listener genie's listen-only forwarding address: no replies, escalation or test mode, plus forwarding setup. */
  listenOnly?: boolean;
}

const GenieMailboxSection = ({
  agentId,
  agentName,
  mailbox,
  brand = "#4E9CFF",
  listenOnly = false,
}: GenieMailboxSectionProps) => {
  const navigate = useNavigate();
  const updateMutation = useUpdateAgentMailboxSelf(agentId);
  const deprovisionMutation = useDeprovisionAgentMailboxSelf(agentId);
  const provisionMutation = useProvisionAgentMailboxSelf(agentId);
  const testMutation = useTestAgentMailboxSelf(agentId);

  const genieSlug = slugify(agentName) || "genie";
  const proposedLocalpart = listenOnly ? `listen-${genieSlug}`.slice(0, 36).replace(/-+$/g, "") : genieSlug;

  const [localpart, setLocalpart] = useState("");
  const [isEditingCreateName, setIsEditingCreateName] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [showDeprovisionConfirm, setShowDeprovisionConfirm] = useState(false);
  const [pendingRenameLocalpart, setPendingRenameLocalpart] = useState<string | null>(null);
  const [isEditingEscalation, setIsEditingEscalation] = useState(false);
  const [escalationAddress, setEscalationAddress] = useState("");

  if (!agentId) return null;

  const domain = mailbox?.email_address?.includes("@") ? mailbox.email_address.split("@")[1] : null;

  const handleCreate = async () => {
    const trimmed = isEditingCreateName ? localpart.trim().toLowerCase() : proposedLocalpart;
    if (!LOCALPART_RE.test(trimmed)) {
      toast.error("Use 2-40 lowercase letters, digits, or hyphens (no leading/trailing hyphen)");
      return;
    }
    try {
      await provisionMutation.mutateAsync({ desiredLocalpart: trimmed });
      toast.success(listenOnly ? "Forwarding address created" : "Mailbox created");
      setLocalpart("");
      setIsEditingCreateName(false);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to create mailbox");
    }
  };

  const handleRename = () => {
    const trimmed = localpart.trim().toLowerCase();
    if (!trimmed) {
      toast.error("Enter a new mailbox name");
      return;
    }
    if (!LOCALPART_RE.test(trimmed)) {
      toast.error("Use 2-40 lowercase letters, digits, or hyphens (no leading/trailing hyphen)");
      return;
    }
    setPendingRenameLocalpart(trimmed);
  };

  const confirmRename = async (keepMailHistory: boolean) => {
    const trimmed = pendingRenameLocalpart;
    if (!trimmed) return;
    try {
      await provisionMutation.mutateAsync({ desiredLocalpart: trimmed, keepMailHistory });
      toast.success("Mailbox renamed");
      setLocalpart("");
      setIsRenaming(false);
      setPendingRenameLocalpart(null);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to rename mailbox");
      setPendingRenameLocalpart(null);
    }
  };

  const handleRecreateAsListener = async () => {
    try {
      await provisionMutation.mutateAsync({ desiredLocalpart: proposedLocalpart });
      toast.success("Forwarding address created");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to recreate the mailbox");
    }
  };

  const handleToggleSuspend = async () => {
    if (!mailbox) return;
    const action = mailbox.status === "suspended" ? "resume" : "suspend";
    try {
      await updateMutation.mutateAsync({ action });
      toast.success(action === "suspend" ? "Mailbox suspended" : "Mailbox resumed");
    } catch (err: any) {
      toast.error(err.message ?? `Failed to ${action} mailbox`);
    }
  };

  const handleDeprovision = async () => {
    try {
      await deprovisionMutation.mutateAsync();
      toast.success("Mailbox deleted");
      setShowDeprovisionConfirm(false);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to delete mailbox");
    }
  };

  const handleSaveEscalation = async () => {
    const trimmed = escalationAddress.trim();
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      toast.error("Enter a valid email address");
      return;
    }
    try {
      await updateMutation.mutateAsync({ escalation_address: trimmed || null });
      toast.success(trimmed ? "Escalation address updated" : "Escalation address cleared");
      setIsEditingEscalation(false);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update escalation address");
    }
  };

  const handleToggleTestMode = async () => {
    if (!mailbox) return;
    const nextTestMode = !mailbox.test_mode;
    try {
      await updateMutation.mutateAsync({ test_mode: nextTestMode });
      toast.success(nextTestMode ? "Test mode enabled" : "Test mode disabled");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update test mode");
    }
  };

  const handleToggleAttachResources = async () => {
    if (!mailbox) return;
    const nextAttachResources = mailbox.attach_resources === false;
    try {
      await updateMutation.mutateAsync({ attach_resources: nextAttachResources });
      toast.success(
        nextAttachResources
          ? "Replies can now include your genie's files and links"
          : "Replies will no longer include files or links",
      );
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update reply attachments");
    }
  };

  const handleSendTest = async () => {
    try {
      const result = await testMutation.mutateAsync();
      toast.success("Test email sent", { description: result.subject });
    } catch (err: any) {
      toast.error(err.message ?? "Failed to send test email");
    }
  };

  const handleCopyAddress = async () => {
    if (!mailbox?.email_address) return;
    try {
      await navigator.clipboard.writeText(mailbox.email_address);
      toast.success("Mailbox address copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const canToggle = mailbox && (mailbox.status === "provisioned" || mailbox.status === "suspended");
  const isSuspended = mailbox?.status === "suspended";
  const isBusy = updateMutation.isPending || deprovisionMutation.isPending || provisionMutation.isPending;

  return (
    <div className="space-y-4">
      {mailbox ? (
        <div className="space-y-1.5">
          {isRenaming ? (
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Mailbox name</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 flex items-center rounded-md border border-gray-300 overflow-hidden">
                  <input
                    type="text"
                    value={localpart}
                    onChange={(e) => setLocalpart(e.target.value)}
                    placeholder={mailbox.email_address?.split("@")[0] ?? "new-name"}
                    autoComplete="off"
                    autoFocus
                    className="flex-1 min-w-0 px-3 py-2 text-sm font-mono outline-none"
                  />
                  {domain && <span className="px-3 py-2 text-sm text-gray-400 bg-gray-50 shrink-0">@{domain}</span>}
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleRename}
                  disabled={isBusy || !localpart.trim()}
                  className="shrink-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
                >
                  {provisionMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  Save
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsRenaming(false);
                    setLocalpart("");
                  }}
                  disabled={isBusy}
                  className="shrink-0"
                >
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Renaming replaces the current mailbox with a new address — you'll choose what happens to its data next.
              </p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/genie-hub/my-genies/${agentId}/mailbox-logs`)}
                className="gap-1.5 shrink-0"
              >
                <History className="h-3.5 w-3.5" />
                Logs
              </Button>
              <div className="flex-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2.5 min-w-0">
                <CopyableEmail
                  email={mailbox.email_address}
                  className="font-mono text-sm text-gray-900"
                  textClassName="truncate"
                />
                <div className="shrink-0 ml-3 flex items-center gap-1.5">
                  {mailbox.test_mode && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                      Test mode
                    </Badge>
                  )}
                  {getStatusBadge(mailbox.status, brand)}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="sm" disabled={isBusy} className="gap-1">
                      Actions
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        setLocalpart(mailbox.email_address?.split("@")[0] ?? "");
                        setIsRenaming(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleToggleSuspend} disabled={!canToggle}>
                      {isSuspended ? <Play className="h-3.5 w-3.5 mr-1.5" /> : <Pause className="h-3.5 w-3.5 mr-1.5" />}
                      {isSuspended ? "Resume" : "Suspend"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSendTest} disabled={testMutation.isPending}>
                      {testMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Send test email
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowDeprovisionConfirm(true)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {!listenOnly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" size="sm" disabled={isBusy} className="gap-1">
                        Advanced
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setEscalationAddress(mailbox.escalation_address ?? "");
                          setIsEditingEscalation(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit escalation address
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleToggleTestMode} disabled={updateMutation.isPending}>
                        <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
                        {mailbox.test_mode ? "Turn off test mode" : "Turn on test mode"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleToggleAttachResources} disabled={updateMutation.isPending}>
                        <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                        {mailbox.attach_resources === false
                          ? "Attach resources to replies"
                          : "Stop attaching resources to replies"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          )}
          {mailbox.last_error && <div className="text-xs text-red-600">{mailbox.last_error}</div>}
          {mailbox.test_mode && (
            <p className="text-xs text-amber-600">
              Test mode is on — escalated conversations go to support@helpgenie.ai instead of the escalation address
              below.
            </p>
          )}
          {!listenOnly && mailbox.attach_resources === false && (
            <p className="text-xs text-gray-500">
              Replies are sent as text only — your genie's Resources and knowledge base files are not attached.
            </p>
          )}

          {!isRenaming && isEditingEscalation && (
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Escalation address</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={escalationAddress}
                  onChange={(e) => setEscalationAddress(e.target.value)}
                  placeholder="owner@example.com"
                  autoComplete="off"
                  autoFocus
                  className="flex-1 min-w-0 px-3 py-2 text-sm rounded-md border border-gray-300 outline-none"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveEscalation}
                  disabled={updateMutation.isPending}
                  className="shrink-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
                >
                  {updateMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  Save
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingEscalation(false);
                    setEscalationAddress(mailbox.escalation_address ?? "");
                  }}
                  disabled={updateMutation.isPending}
                  className="shrink-0"
                >
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Escalated conversations are sent here — currently{" "}
                <span className="font-medium">{mailbox.escalation_address ?? "the genie owner (default)"}</span>.
              </p>
            </div>
          )}

          {listenOnly && !isRenaming && canToggle && mailbox.mode !== "listen" && (
            <div className="mt-2 rounded-xl border border-genie-secondary/30 bg-genie-secondary/5 p-4 space-y-3">
              <p className="text-sm text-gray-700">
                This mailbox was set up before email listening and still answers email by itself. Recreate it as a
                listen-only forwarding address before you forward your support inbox to it.
              </p>
              <Button
                type="button"
                size="sm"
                onClick={handleRecreateAsListener}
                disabled={isBusy}
                className="min-h-[44px] sm:min-h-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
              >
                {provisionMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Recreate as {proposedLocalpart}@{domain ?? "mail.helpgenie.ai"}
              </Button>
            </div>
          )}

          {!listenOnly && !isRenaming && mailbox.status === "provisioned" && mailbox.email_address && (
            <div className="space-y-3 pt-2">
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-sm font-semibold text-gray-900">Send your support email here</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  Forward your support inbox to this address and your genie answers each email from its knowledge
                  base. Nothing to connect or log in to.
                </p>
                <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-1">
                  <CopyableEmail email={mailbox.email_address} className="font-mono text-sm text-gray-900" />
                </div>
                <EmailForwardingSteps agentId={agentId} address={mailbox.email_address} mode="reply" />
              </div>
              <div className="flex items-start gap-2 text-xs text-gray-500">
                <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-genie-secondary" />
                <p>
                  Put this address on your website's contact page and in your email signature too, so customers can
                  email your genie directly.
                </p>
              </div>
            </div>
          )}

          {listenOnly && !isRenaming && canToggle && mailbox.mode === "listen" && (
            <div className="pt-2">
              <ListenerEmailForwarding agentId={agentId} mailbox={mailbox} />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            {listenOnly
              ? "Create a forwarding address, then forward your support inbox to it. Your genie reads every email and flags what needs you."
              : "No mailbox exists yet for this genie."}
          </p>

          {isEditingCreateName ? (
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Mailbox name</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 flex items-center rounded-md border border-gray-300 overflow-hidden">
                  <input
                    type="text"
                    value={localpart}
                    onChange={(e) => setLocalpart(e.target.value)}
                    placeholder={proposedLocalpart}
                    autoComplete="off"
                    autoFocus
                    className="flex-1 min-w-0 px-3 py-2 text-sm font-mono outline-none"
                  />
                  <span className="px-3 py-2 text-sm text-gray-400 bg-gray-50 shrink-0">@mail.helpgenie.ai</span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCreate}
                  disabled={isBusy || !localpart.trim()}
                  className="shrink-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
                >
                  {provisionMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  {listenOnly ? "Create forwarding address" : "Create mailbox"}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">2-40 lowercase letters, digits, or hyphens.</p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="flex-1 flex items-center justify-between rounded-md border border-dashed border-gray-300 px-3 py-2.5 min-w-0">
                <span className="font-mono text-sm text-gray-500 truncate">{proposedLocalpart}@mail.helpgenie.ai</span>
                <button
                  type="button"
                  onClick={() => {
                    setLocalpart(proposedLocalpart);
                    setIsEditingCreateName(true);
                  }}
                  className="text-xs font-medium shrink-0 ml-3 hover:underline"
                  style={{ color: brand }}
                >
                  Change name
                </button>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleCreate}
                disabled={isBusy}
                className="shrink-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
              >
                {provisionMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                {listenOnly ? "Create forwarding address" : "Create mailbox"}
              </Button>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={showDeprovisionConfirm} onOpenChange={setShowDeprovisionConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this mailbox?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <CopyableEmail email={mailbox?.email_address} className="font-medium" /> in HG
              Mail. The genie will stop receiving email until a new mailbox is created. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeprovision}
              disabled={deprovisionMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingRenameLocalpart} onOpenChange={(open) => !open && setPendingRenameLocalpart(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename this mailbox?</AlertDialogTitle>
            <AlertDialogDescription>
              Renaming replaces <CopyableEmail email={mailbox?.email_address} className="font-medium" /> with a new
              address in HG Mail. Choose what happens to the old mailbox's data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-col sm:items-stretch sm:gap-2">
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmRename(false)}
              disabled={provisionMutation.isPending}
            >
              {provisionMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Delete permanently
            </AlertDialogAction>
            <Button
              type="button"
              variant="outline"
              onClick={() => confirmRename(true)}
              disabled={provisionMutation.isPending}
            >
              Keep history in HG Mail
            </Button>
            <AlertDialogCancel disabled={provisionMutation.isPending}>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
          <p className="text-xs text-gray-400 -mt-2">
            "Delete permanently" removes the old mailbox and its decisions/threads history from HG Mail. "Keep history"
            leaves that data in HG Mail, unlinked from this genie. Either way, this cannot be undone.
          </p>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GenieMailboxSection;
